import * as React from 'react';

import {useLoadDashboard} from 'src/components/analytics-genesis/byod/api';
import {reportByodApiError} from 'src/components/analytics-genesis/byod/api/error-handling';
import {hydrateDashboardFromApi} from 'src/components/analytics-genesis/byod/store/hydrateDashboard';
import type {HydrationResult} from 'src/components/analytics-genesis/byod/store/hydrateDashboard';
import {
  useDashboardViewStore,
  useDashboardViewEntry,
} from 'src/components/analytics-genesis/byod/store/dashboardViewStore';


export type UseByodDashboardDataReturn = {
  /**
   * Still resolving: the network load is in flight, OR it resolved but the
   * hydration effect hasn't written the store entry yet. The view shows one
   * loader across that whole gap.
   */
  isLoading: boolean;
  /** Network load failed — retrying the request may recover it. */
  isError: boolean;
  /** Re-run the underlying `/dashboards/:id` load. */
  refetch: () => void;
  /** Hydrated dashboard data, or `null` before the first successful hydration. */
  hydrated: HydrationResult | null;
  /** Load succeeded but hydration threw (a parse failure — non-retryable). */
  hydrationFailed: boolean;
};

/**
 * Owns the embed's dashboard-data lifecycle so the view stays presentational:
 * load `/dashboards/:id`, report load failures to Sentry (once per broken id),
 * hydrate the parsed payload into the keyed `dashboardViewStore`, and report
 * hydration/parse failures. All network + telemetry + persistence concerns live
 * here; the caller only reads the resulting display state.
 */
export function useByodDashboardData(
  dashboardId: string,
): UseByodDashboardDataReturn {
  const {data, isLoading, isError, error, refetch} =
    useLoadDashboard(dashboardId);

  // Surface dashboard-load failures to Sentry. The embed can render on a host
  // page where no one is watching the builder, so the danger banner alone isn't
  // enough — report so a broken embed is observable. (useApiQuery's non-suspense
  // path re-throws without capturing.) Report at most once per failed dashboard:
  // React Query hands a fresh error object across refetch cycles, so guard on a
  // ref to avoid re-reporting the same broken embed. Aborted/cancelled requests
  // are filtered inside reportByodApiError.
  const reportedLoadErrorForRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (isError && error) {
      if (reportedLoadErrorForRef.current !== dashboardId) {
        reportByodApiError(error, {action: 'load-dashboard', dashboardId});
        reportedLoadErrorForRef.current = dashboardId;
      }
    } else {
      reportedLoadErrorForRef.current = null;
    }
  }, [isError, error, dashboardId]);

  // Per-dashboard hydrated data lives in the keyed `dashboardViewStore`
  // (`byId[dashboardId]`) rather than local component state, so each dashboard
  // id is linked to its hydrated data and N embeds of the same id share one
  // entry. Per-instance inputs (date window, filters) stay outside this store —
  // they're not intrinsic to the dashboard and would clash across same-id
  // instances.
  const {hydrated, hydrationFailed} = useDashboardViewEntry(dashboardId);
  const setHydrated = useDashboardViewStore((s) => s.setHydrated);
  const setHydrationFailed = useDashboardViewStore((s) => s.setHydrationFailed);

  // Hydrate on load, in an effect because hydrateDashboardFromApi reports
  // orphaned widgets via Sentry (a side effect that must not run during render).
  // Guarded: a load can succeed yet hydration throw on a malformed payload —
  // that's a data/parse failure, not a network one, so a throw here must not
  // become an uncaught effect error (the error boundary can't catch effect
  // throws). Report it and flag a distinct, non-retryable failure state. When
  // `data` is absent (initial load) the id's existing entry is left untouched —
  // the key already isolates dashboards, and the loading / error branches in the
  // view own the "no data yet" UI.
  React.useEffect(() => {
    if (!data) {
      return;
    }
    try {
      setHydrated(dashboardId, hydrateDashboardFromApi(data));
    } catch (err) {
      setHydrationFailed(dashboardId);
      reportByodApiError(err, {action: 'hydrate-dashboard', dashboardId});
    }
  }, [data, dashboardId, setHydrated, setHydrationFailed]);

  const retry = React.useCallback(() => void refetch(), [refetch]);

  // Fold the pre-hydration gap into `isLoading`: a load can have resolved
  // (`data` present) while the hydration effect hasn't run yet — keep showing
  // the loader through that window rather than flashing an empty canvas.
  const isHydrationPending = Boolean(data) && !hydrated && !hydrationFailed;

  return {
    isLoading: isLoading || isHydrationPending,
    isError,
    refetch: retry,
    hydrated,
    hydrationFailed,
  };
}
